import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useEmpresa } from "@/context/EmpresaContext";
import api from "@/lib/api";
import {
  LayoutDashboard, Users, Package, ShoppingCart,
  Receipt, Banknote, Settings, LogOut, Building2, Repeat,
  FileSpreadsheet, Wrench, PieChart, ChevronsUpDown, ClipboardList,
  Menu, X, Mail, Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import NotificacoesBell from "@/components/NotificacoesBell";
import NotasPresasBadge from "@/components/NotasPresasBadge";
import CertificadoAviso from "@/components/CertificadoAviso";
import EmailFalhoBell from "@/components/EmailFalhoBell";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", tid: "nav-dashboard" },
  { to: "/clientes", icon: Users, label: "Clientes", tid: "nav-clientes" },
  { to: "/orcamentos", icon: ClipboardList, label: "Orçamentos", tid: "nav-orcamentos" },
  { to: "/produtos", icon: Package, label: "Cadastro de Produtos", tid: "nav-produtos" },
  { to: "/servicos", icon: Wrench, label: "Cadastro de Serviços", tid: "nav-servicos" },
  { to: "/vendas", icon: ShoppingCart, label: "Vendas de Produtos", tid: "nav-vendas" },
  { to: "/vendas-servicos", icon: FileSpreadsheet, label: "Vendas de Serviços", tid: "nav-vendas-servicos" },
  { to: "/nfe", icon: FileSpreadsheet, label: "NF-e (Produtos)", tid: "nav-nfe" },
  { to: "/nfse", icon: Wrench, label: "NFS-e (Serviços)", tid: "nav-nfse" },
  { to: "/boletos", icon: Receipt, label: "Contas a Receber", tid: "nav-boletos" },
  { to: "/contas-a-receber", icon: Banknote, label: "Extrato Financeiro", tid: "nav-contas-a-receber" },
  { to: "/assinaturas", icon: Repeat, label: "Assinaturas", tid: "nav-assinaturas" },
  { to: "/emails", icon: Mail, label: "Envios de E-mail", tid: "nav-emails" },
  { to: "/gestao", icon: Activity, label: "Monitor e Cobrança", tid: "nav-gestao" },
  { to: "/dre", icon: PieChart, label: "DRE Mensal", tid: "nav-dre" },
  { to: "/configuracoes", icon: Settings, label: "Configurações", tid: "nav-config" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { empresas, empresaAtualId, empresaAtual, selecionar } = useEmpresa();
  const nav = useNavigate();
  const location = useLocation();

  const empresasAtivas = empresas.filter((e) => e.ativa !== false);

  // v10.39 — Mobile responsive sidebar drawer
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Fecha o drawer ao trocar de rota (ex: usuário clica num link do menu)
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);
  // Bloqueia scroll do body quando drawer aberto no mobile
  useEffect(() => {
    if (sidebarOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [sidebarOpen]);

  // Contagem de boletos emitidos (abertos + pagos) para a saudação no topo.
  // Atualiza quando o usuário troca de empresa e a cada 60s.
  const [boletosEmitidos, setBoletosEmitidos] = useState(null);
  useEffect(() => {
    const carregar = () => {
      api.get("/dashboard")
        .then((r) => {
          const k = r.data?.kpis || {};
          const total = (Number(k.boletos_abertos) || 0) + (Number(k.boletos_pagos) || 0);
          setBoletosEmitidos(total);
        })
        .catch(() => setBoletosEmitidos(null));
    };
    carregar();
    const t = setInterval(carregar, 60000);
    return () => clearInterval(t);
  }, [empresaAtualId]);

  const primeiroNome = (user?.name || user?.email || "").split(" ")[0] || "";

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Backdrop mobile — só aparece quando drawer aberto */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop"
          aria-hidden="true"
        />
      )}

      <aside
        data-testid="sidebar"
        className={`fixed inset-y-0 left-0 z-50 w-72 shrink-0 border-r border-slate-200 bg-white flex flex-col transform transition-transform duration-200 ease-out md:static md:w-64 md:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
      >
        <div className="px-5 py-6 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-200 grid place-items-center">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-lg leading-none text-slate-900">
                DigiFácil
              </div>
              <div className="text-xs text-slate-500 mt-1">NF-e • Boletos • Fiscal</div>
            </div>
            {/* Botão fechar — só aparece no mobile */}
            <button
              type="button"
              className="md:hidden p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
              onClick={() => setSidebarOpen(false)}
              data-testid="sidebar-close"
              aria-label="Fechar menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Seletor de empresa */}
          <div className="mt-4" data-testid="empresa-selector-wrap">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1">
              <ChevronsUpDown className="w-3 h-3" /> Empresa Ativa
            </div>
            {empresasAtivas.length === 0 ? (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                Cadastre uma empresa em <strong>Configurações → Empresas</strong>
              </div>
            ) : (
              <Select value={empresaAtualId || ""} onValueChange={(id) => {
                // v10.28 — Recarregar a página ao trocar empresa pra re-carregar
                // listas (boletos, notas, assinaturas etc) com o filtro certo.
                selecionar(id);
                if (id && id !== empresaAtualId) {
                  // pequeno delay pro localStorage persistir antes do reload
                  setTimeout(() => window.location.reload(), 50);
                }
              }}>
                <SelectTrigger data-testid="empresa-selector" className="w-full">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {empresasAtivas.map((e) => (
                    <SelectItem key={e.id} value={e.id} data-testid={`empresa-opt-${e.id}`}>
                      {e.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {empresaAtual && (
              <div className="text-[11px] text-slate-500 mt-1 font-mono-fin truncate" data-testid="empresa-atual-cnpj">
                CNPJ {empresaAtual.documento}
              </div>
            )}
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.filter((n) => {
            // Módulos protegidos por permissoes_modulos (fallback compat: /assinaturas usa emite_assinatura)
            const permKey = { "/produtos": "produtos", "/servicos": "servicos",
              "/vendas": "vendas", "/vendas-servicos": "vendas-servicos",
              "/nfe": "nfe", "/nfse": "nfse", "/assinaturas": "assinaturas" }[n.to];
            if (permKey && empresaAtual?.permissoes_modulos && permKey in empresaAtual.permissoes_modulos) {
              return empresaAtual.permissoes_modulos[permKey] !== false;
            }
            // fallback antigo: /assinaturas usa emite_assinatura
            if (n.to === "/assinaturas") return !!empresaAtual?.emite_assinatura;
            return true;
          }).map(({ to, icon: Icon, label, tid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={tid}
              className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}
            >
              <Icon className="w-[18px] h-[18px] shrink-0" />
              <span className="text-sm">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="text-xs text-slate-500 mb-2">Conectado como</div>
          <div className="text-sm font-semibold text-slate-800 truncate">{user?.name}</div>
          <div className="text-xs text-blue-600 uppercase font-mono-fin mb-3">{user?.role}</div>
          <Button
            variant="outline"
            size="sm"
            className="w-full border-slate-200 text-slate-700 hover:bg-slate-100"
            onClick={async () => { await logout(); nav("/login"); }}
            data-testid="btn-logout"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sair
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto min-w-0 w-full">
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200 px-3 sm:px-5 lg:px-8 py-2.5 sm:py-3 flex items-center justify-between gap-2 sm:gap-3">
          {/* Botão hamburger — só mobile */}
          <button
            type="button"
            className="md:hidden p-2 rounded-md hover:bg-slate-100 text-slate-700 shrink-0"
            onClick={() => setSidebarOpen(true)}
            data-testid="btn-sidebar-open"
            aria-label="Abrir menu"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Lado esquerdo: saudação + KPI rápido */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1" data-testid="header-greeting">
            <div className="hidden sm:grid w-9 h-9 rounded-full bg-blue-50 border border-blue-200 place-items-center shrink-0">
              <Building2 className="w-4 h-4 text-blue-600" />
            </div>
            <div className="min-w-0">
              <div className="text-sm sm:text-[15px] font-semibold text-slate-800 truncate leading-tight" data-testid="header-boas-vindas">
                <span className="hidden sm:inline">Bem-vindo ao </span>
                <span className="text-blue-700">DigiFácil</span>
                {primeiroNome && <span className="text-slate-500 font-normal hidden sm:inline">, {primeiroNome}</span>}
              </div>
              <div className="text-[10px] sm:text-[11px] text-slate-500 flex items-center gap-1.5 mt-0.5" data-testid="header-boletos-emitidos">
                <Receipt className="w-3 h-3 text-blue-600 shrink-0" />
                {boletosEmitidos == null
                  ? "carregando…"
                  : (
                    <span className="truncate">
                      <strong className="text-slate-800 font-mono-fin">{boletosEmitidos}</strong>
                      {" "}boleto{boletosEmitidos === 1 ? "" : "s"}
                      <span className="hidden sm:inline"> emitido{boletosEmitidos === 1 ? "" : "s"}</span>
                      {empresaAtual && <span className="text-slate-400 hidden md:inline">&nbsp;•&nbsp;{empresaAtual.nome_fantasia || empresaAtual.nome}</span>}
                    </span>
                  )}
              </div>
            </div>
          </div>

          {/* Lado direito: sinos de notificações + usuário + sair */}
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            <CertificadoAviso />
            <NotasPresasBadge />
            <NotificacoesBell filter="boleto" />
            <NotificacoesBell filter="nota" />
            <EmailFalhoBell />
            <div className="hidden sm:block h-6 w-px bg-slate-200" />
            <div className="hidden md:block text-right">
              <div className="text-xs text-slate-400 leading-none">Logado como</div>
              <div className="text-sm font-semibold text-slate-800 truncate max-w-[220px]">{user?.name || user?.email}</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => { await logout(); nav("/login"); }}
              data-testid="btn-logout-top"
              className="border-slate-200 text-slate-700 hover:bg-slate-100 h-8 px-2 sm:h-9 sm:px-3"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Sair</span>
            </Button>
          </div>
        </header>
        <div className="p-3 sm:p-4 md:p-6 lg:p-8 max-w-[1600px]">{children}</div>
      </main>
    </div>
  );
}
